
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Language } from '../types';
import { t } from '../locales';
import { Star } from 'lucide-react';

interface FavoriteNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, time: string | undefined) => void;
  initialName?: string;
  initialTime?: string;
  language: Language;
}

const FavoriteNameModal: React.FC<FavoriteNameModalProps> = ({ isOpen, onClose, onSave, initialName = '', initialTime = '', language }) => {
  const [name, setName] = useState(initialName);
  const [time, setTime] = useState(initialTime);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl shadow-2xl p-6 relative z-10 animate-popIn border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg text-yellow-600">
                <Star size={20} fill="currentColor" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-white">{t(language, 'addFavorite')}</h3>
        </div>
        
        <div className="flex flex-col gap-4">
            <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    {t(language, 'favoriteName')}
                </label>
                <input 
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Daily Oatmeal"
                    className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                    autoFocus
                />
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    {t(language, 'usualTime')}
                </label>
                <input 
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-yellow-500 outline-none transition-all"
                />
            </div>
            
            <div className="flex gap-3 mt-2">
                <button 
                    onClick={onClose}
                    className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                    {t(language, 'cancel')}
                </button>
                <button 
                    onClick={() => { if(name.trim()) { onSave(name, time); onClose(); } }}
                    disabled={!name.trim()}
                    className="flex-1 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-white font-bold shadow-lg transition-transform active:scale-95 disabled:opacity-50"
                >
                    {t(language, 'save')}
                </button>
            </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FavoriteNameModal;
